/**
 * Serené Cart Rewards
 * Liquid renderiza el estado correcto del carrito (mensaje, progress, gifts) en cada
 * section update del Shrine Pro. Este JS se encarga de:
 *   - countdown timer (localStorage, persistente, reinicia al expirar)
 *   - auto-add / auto-remove de productos regalo según thresholds
 *   - ocultar visualmente los gift line items dentro de la lista normal
 *
 * No intercepta botones nativos. El checkout usa el botón nativo del tema /
 * EasySell directamente — no hay CTA personalizado.
 *
 * Anti-loop: mutex `busy` + cooldown 350ms + se ignora a sí mismo en fetch hook.
 */
(function () {
  'use strict';

  if (window.SereneCartRewards) return;

  const CONFIG = {
    thresholds: {
      shipping: 7990000,  // $79.900 en cents COP
      gift1:    11500000, // $115.000 (único umbral de regalo)
    },
    // Único producto regalo: "Decant Serené (Regalo)" — variant $0 dedicada.
    // Handle: decant-serene-regalo. Variant ID obtenido vía Shopify API.
    // No usa descuentos automáticos — precio nativo $0.
    // Lógica:
    //   subtotal >= threshold1 ($99.000) → qty 1
    //   else                             → qty 0
    gifts: [
      {
        key: 'decant',
        variantId: 45300045283362,
        threshold1: 11500000,
        legacyVariantIds: [
          45299821674530,  // Bono Serené 15 mil próxima compra (descontinuado)
          45197136625698,  // Tarjeta Regalo Serené 15 mil (descontinuada)
          45296885727266,  // Tarjeta • Regalo $1 (descontinuada)
          45296888578082,  // Decant • Regalo $1 (descontinuada)
          45061946310690,  // Decant Serené regular $29.900 (ya no se usa como regalo)
        ],
        handles: ['decant-serene-regalo'],
        legacyHandles: [
          'bono-serene-15-mil-proxima-compra',
          'tarjeta-regalo-serene-15-mil',
          'tarjeta-regalo-serene-15-mil-regalo',
          'decant-serene-regalo-gratis',
          'decant-serene-1',
        ],
      },
    ],
    // Kit Serené 30 ml — regalo "Fragancia Sorpresa" ($0, variant dedicada).
    // NO es threshold-based: solo debe permanecer mientras el kit (8 fragancias
    // de 30 ml) siga en el carrito. Si el cliente vacía las fragancias del kit
    // y compra otra cosa (ej. 2x 100 ml), la sorpresa se retira automáticamente.
    // Esto corre GLOBALMENTE (cart-rewards.js carga en todas las páginas),
    // a diferencia de kit-builder-30ml.js que solo existe en la colección del kit.
    kitSurprise: {
      variantId: 45309590044706,         // fragancia-sorpresa-serene-col-30-ml ($0)
      target: 8,                         // = KitBuilder.config.target (8 fragancias)
      stateKey: 'serene_kit_30ml_state', // localStorage de kit-builder-30ml.js
    },
    countdownSeconds: 600,
    storageCountdownKey: 'cr_countdown_end_v2', // localStorage key (v2 = post-migración desde sessionStorage)
  };

  const STATE = {
    busy: false,
    lastReconcile: 0,
    initialized: false,
    countdownInterval: null,
    lastCart: null,
  };

  // Defense #1 (v3.10): Promise-based mutex para reconcileGifts.
  // STATE.busy puede ser reseteado por fetchCart() externo, lo cual abre la
  // ventana de race que causa la multiplicación del decant. Este lock NO se
  // puede sobreescribir desde fuera del reconcile — solo se libera cuando el
  // reconcile termina realmente.
  let _reconcileLock = null;

  // ---------- Helpers ----------
  function isGiftItem(item) {
    if (!item || !item.properties) return false;
    return item.properties._gift === 'true' || item.properties._gift === true;
  }

  function subtotalExcludingGifts(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    const giftVariantIds = new Set();
    CONFIG.gifts.forEach(g => {
      giftVariantIds.add(String(g.variantId));
      (g.legacyVariantIds || []).forEach(id => giftVariantIds.add(String(id)));
    });
    return cart.items.reduce((sum, item) => {
      if (isGiftItem(item)) return sum;
      if (giftVariantIds.has(String(item.variant_id))) return sum;
      return sum + (item.line_price || item.original_line_price || 0);
    }, 0);
  }

  // ---------- Cart API ----------
  async function fetchCart() {
    try {
      STATE.busy = true; // bloquear el hook durante esta lectura
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
      });
      return await res.json();
    } catch (e) {
      return null;
    } finally {
      STATE.busy = false;
    }
  }

  async function changeItem(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity }),
      });
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  async function addLine(payload) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  // ---------- Kit Serené 30 ml: limpieza de la Fragancia Sorpresa ----------
  // Lee el set de variant IDs que formaron el kit desde el localStorage de
  // kit-builder-30ml.js. Devuelve null si no hay estado (otro navegador, borrado,
  // etc.) para que el llamador caiga al fallback por conteo de items.
  function getKitVariantSet() {
    try {
      const raw = localStorage.getItem(CONFIG.kitSurprise.stateKey);
      if (!raw) return null;
      const s = JSON.parse(raw);
      if (!s || !Array.isArray(s.items) || !s.items.length) return null;
      const set = new Set();
      s.items.forEach(i => { if (i && i.variantId != null) set.add(String(i.variantId)); });
      return set.size ? set : null;
    } catch (e) { return null; }
  }

  // ¿El kit de 30 ml SIGUE presente en el carrito?
  // - Con estado local: cuenta las cantidades de líneas (no-sorpresa) cuyo
  //   variant pertenece al kit guardado. Intersecta con la realidad del carrito,
  //   así que funciona aunque el estado local esté "stale" (no se actualiza al
  //   borrar items fuera de la página del kit).
  // - Sin estado local (fallback): cuenta los items que NO son la sorpresa.
  //   Cubre el caso del usuario (borra el kit + agrega 2x 100 ml → quedan <8).
  function kitStillComplete(cart) {
    const ks = CONFIG.kitSurprise;
    const surpriseId = String(ks.variantId);
    const items = (cart && cart.items) || [];
    const kitSet = getKitVariantSet();
    if (kitSet) {
      let count = 0;
      items.forEach(it => {
        if (String(it.variant_id) === surpriseId) return;
        if (kitSet.has(String(it.variant_id))) count += it.quantity;
      });
      return count >= ks.target;
    }
    let others = 0;
    items.forEach(it => { if (String(it.variant_id) !== surpriseId) others += it.quantity; });
    return others >= ks.target;
  }

  // Retira la Fragancia Sorpresa si el kit ya no está completo. Devuelve true si mutó.
  // Idempotente: si no hay sorpresa en el carrito, o el kit sigue completo, no hace nada.
  async function reconcileKitSurprise(cart) {
    const surpriseId = String(CONFIG.kitSurprise.variantId);
    const surpriseLines = ((cart && cart.items) || []).filter(
      it => String(it.variant_id) === surpriseId
    );
    if (surpriseLines.length === 0) return false; // no hay regalo → nada que hacer
    if (kitStillComplete(cart)) return false;      // kit intacto → conservar regalo
    let mutated = false;
    for (const line of surpriseLines) {            // kit incompleto → remover sorpresa
      await changeItem(line.key, 0);
      mutated = true;
    }
    return mutated;
  }

  // ---------- Reconcile gifts ----------
  // Lógica simplificada (1 producto único, qty máxima = 1):
  //   subtotal >= threshold1 → qty 1
  //   else                   → qty 0 (sin regalo)
  // Mantiene SIEMPRE una sola línea con cantidad 1.
  // - Si la línea no existe y desiredQty=1 → /cart/add.js
  // - Si existe con qty != 1 → /cart/change.js con qty=1
  // - Si hay duplicados → consolida en una sola línea
  // - Si desiredQty=0 → remueve todas las líneas del regalo
  async function reconcileGifts() {
    // Defense #1 (v3.10): Promise-based mutex. Si ya hay un reconcile en vuelo,
    // retornamos su Promise. Esto IMPIDE que dos reconciles corran en paralelo,
    // incluso si fetchCart() externo resetea STATE.busy a mitad de camino.
    if (_reconcileLock) return _reconcileLock;
    if (Date.now() - STATE.lastReconcile < 350) return false;

    _reconcileLock = (async () => {
      STATE.busy = true;
      let mutated = false;

      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
        const cart = await res.json();
        if (!cart) return false;

        const subtotal = subtotalExcludingGifts(cart);

        for (const gift of CONFIG.gifts) {
          // Cantidad objetivo: 1 si supera el umbral, 0 si no.
          let desiredQty = 0;
          if (gift.threshold1 != null && subtotal >= gift.threshold1) {
            desiredQty = 1;
          }

          const legacyVariantIds = (gift.legacyVariantIds || []).map(String);
          const legacyHandles = gift.legacyHandles || [];
          const goodVariantId = String(gift.variantId);

          // Items legacy en el cart (variantes/handles descontinuados → siempre se remueven)
          const legacyMatches = cart.items.filter(it =>
            legacyVariantIds.includes(String(it.variant_id)) ||
            (it.handle && legacyHandles.includes(it.handle))
          );

          // Líneas actuales del regalo (variant correcto)
          const goodMatches = cart.items.filter(it =>
            String(it.variant_id) === goodVariantId
          );

          // 1) Limpiar legacy
          for (const m of legacyMatches) {
            await changeItem(m.key, 0);
            mutated = true;
          }

          // 2) Ajustar línea(s) del regalo a la qty objetivo
          if (desiredQty === 0) {
            // No debe haber regalo
            for (const m of goodMatches) {
              await changeItem(m.key, 0);
              mutated = true;
            }
          } else if (goodMatches.length === 0) {
            // Defense #2 (v3.10): re-fetch cart RIGHT BEFORE add. Si otro path
            // (race) metió el decant durante nuestro reconcile, ajustamos en
            // vez de agregar otro.
            try {
              const verifyRes = await fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
              const verifyCart = await verifyRes.json();
              const verifyMatches = ((verifyCart && verifyCart.items) || []).filter(it =>
                String(it.variant_id) === goodVariantId
              );
              if (verifyMatches.length === 0) {
                // Genuino: cart sigue sin decant, podemos agregar UNA
                await addLine({ id: gift.variantId, quantity: desiredQty });
                mutated = true;
              } else {
                // Race detectada — el decant ya está. Consolidar y ajustar qty.
                const first = verifyMatches[0];
                for (let i = 1; i < verifyMatches.length; i++) {
                  await changeItem(verifyMatches[i].key, 0);
                  mutated = true;
                }
                if (first.quantity !== desiredQty) {
                  await changeItem(first.key, desiredQty);
                  mutated = true;
                }
              }
            } catch (e) {
              // Si la verificación falla, ejecutamos el add original (mejor que skip)
              await addLine({ id: gift.variantId, quantity: desiredQty });
              mutated = true;
            }
          } else {
            // Hay 1+ líneas: consolidar en la primera con qty correcta, eliminar duplicados
            const first = goodMatches[0];
            for (let i = 1; i < goodMatches.length; i++) {
              await changeItem(goodMatches[i].key, 0);
              mutated = true;
            }
            if (first.quantity !== desiredQty) {
              await changeItem(first.key, desiredQty);
              mutated = true;
            }
          }
        }

        // Kit Serené 30 ml: retirar la Fragancia Sorpresa si el kit ya no está
        // completo (cliente vació las fragancias y compró otra cosa). Comparte el
        // mismo lock/mutex → sin carreras con la lógica del decant de arriba.
        if (await reconcileKitSurprise(cart)) mutated = true;

        // Defense #3 (v3.10): HARD FLOOR — re-verificar al final que NUNCA haya > 1.
        // Esta es la red de seguridad final: incluso si todo lo anterior falló,
        // garantizamos que al salir del reconcile el cart tiene máximo 1 decant qty=1.
        try {
          const finalRes = await fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
          const finalCart = await finalRes.json();
          for (const gift of CONFIG.gifts) {
            const gid = String(gift.variantId);
            const finalMatches = ((finalCart && finalCart.items) || []).filter(it =>
              String(it.variant_id) === gid
            );
            if (finalMatches.length === 0) continue;
            // Consolidar duplicados: dejar solo el primero
            if (finalMatches.length > 1) {
              for (let i = 1; i < finalMatches.length; i++) {
                await changeItem(finalMatches[i].key, 0);
                mutated = true;
              }
            }
            // Force qty = 1 si está > 1
            if (finalMatches[0].quantity > 1) {
              await changeItem(finalMatches[0].key, 1);
              mutated = true;
            }
          }
        } catch (e) { /* hard floor failed silently, no rompemos el resto */ }

        STATE.lastReconcile = Date.now();
        return mutated;
      } finally {
        STATE.busy = false;
        _reconcileLock = null;  // Liberar el lock SOLO cuando termina realmente
      }
    })();

    return _reconcileLock;
  }

  // ---------- Render progress + message + milestones (JS overlay) ----------
  function formatCOP(cents) {
    if (cents == null || isNaN(cents)) return '$0';
    const pesos = Math.round(cents / 100);
    return '$' + pesos.toLocaleString('es-CO');
  }

  function renderRewardsFromCart(cart) {
    if (!cart) return;

    const subtotal = (cart.total_price || 0); // en centavos
    const subtotalWithoutGifts = subtotalExcludingGifts(cart);
    const subtotalPesos = subtotal / 100;
    const subtotalSinRegalosPesos = subtotalWithoutGifts / 100;
    // Progreso 0-100 hacia el único umbral de regalo ($115.000)
    const progress = Math.min((subtotalSinRegalosPesos / 115000) * 100, 100);

    // DEBUG
    try {
      console.log('[CartRewards] Subtotal:', subtotalPesos);
      console.log('[CartRewards] Subtotal sin regalos:', subtotalSinRegalosPesos);
      console.log('[CartRewards] Progreso:', progress.toFixed(2) + '%');
    } catch (e) {}

    // Progress fill — escribir directo en el DOM (cubre cualquier failover de Liquid)
    document.querySelectorAll('[data-cr-fill]').forEach(el => {
      el.style.setProperty('width', progress + '%', 'important');
    });

    // Aria
    document.querySelectorAll('[role="progressbar"]').forEach(el => {
      if (el.closest('.cr-progress')) el.setAttribute('aria-valuenow', Math.round(progress));
    });

    // Subtotal data attr
    document.querySelectorAll('[data-cr-root]').forEach(el => {
      el.dataset.crSubtotal = subtotalWithoutGifts;
    });

    // Milestones reached (solo 2: envío gratis + regalo)
    const T = CONFIG.thresholds;
    const reached = [
      subtotalWithoutGifts >= T.shipping,
      subtotalWithoutGifts >= T.gift1,
    ];
    document.querySelectorAll('[data-cr-milestone]').forEach((el) => {
      const idx = parseInt(el.dataset.crMilestone, 10);
      if (Number.isFinite(idx) && reached[idx] !== undefined) {
        el.classList.toggle('cr-milestone--reached', reached[idx]);
      }
    });

    // Mensaje dinámico — envío SIEMPRE gratis (badge fijo en Liquid), solo progreso del regalo
    const msgEl = document.querySelector('[data-cr-message]');
    if (msgEl) {
      let html, state;
      if (subtotalWithoutGifts < T.gift1) {
        html = `Te faltan <strong>${formatCOP(T.gift1 - subtotalWithoutGifts)}</strong> para desbloquear tu regalo 🎁`;
        state = 'gift';
      } else {
        html = `🎉 ¡Ya desbloqueaste tu regalo!`;
        state = 'complete';
      }
      msgEl.innerHTML = html;
      msgEl.dataset.crState = state;
    }
  }

  // ---------- Hide gift line items in the regular list ----------
  function markGiftLineItems(cart) {
    if (!cart) return;
    const giftKeys = new Set();
    const allGiftHandles = new Set();
    CONFIG.gifts.forEach(g => (g.handles || []).forEach(h => allGiftHandles.add(h)));
    cart.items.forEach(it => {
      const isVariantGift = CONFIG.gifts.some(g =>
        String(it.variant_id) === String(g.variantId) ||
        (g.legacyVariantIds || []).map(String).includes(String(it.variant_id))
      );
      const isHandleGift = it.handle && allGiftHandles.has(it.handle);
      if (isGiftItem(it) || isVariantGift || isHandleGift) giftKeys.add(it.key);
    });
    document.querySelectorAll('.cart-drawer .cart-drawer-item').forEach(li => {
      const key = li.dataset.key || (li.id || '').match(/Item-(\d+)/)?.[1];
      const handleClass = Array.from(li.classList).find(c => c.startsWith('cart-item--product-'));
      const handle = handleClass ? handleClass.replace('cart-item--product-', '') : '';
      const isGift = (key && giftKeys.has(key)) || allGiftHandles.has(handle);
      li.dataset.crGiftLine = isGift ? 'true' : 'false';
    });
  }

  // ---------- Countdown (localStorage, persistente, reinicia al expirar) ----------
  function readEndTime() {
    try {
      return parseInt(localStorage.getItem(CONFIG.storageCountdownKey) || '0', 10) || 0;
    } catch (e) { return 0; } // localStorage puede fallar en modo privado / iframes
  }
  function writeEndTime(t) {
    try { localStorage.setItem(CONFIG.storageCountdownKey, String(t)); } catch (e) {}
  }

  function startCountdown() {
    let endTime = readEndTime();
    const now = Date.now();
    // Si no hay countdown o ya expiró → reiniciar con un nuevo bloque de 10 minutos
    if (!endTime || endTime <= now) {
      endTime = now + (CONFIG.countdownSeconds * 1000);
      writeEndTime(endTime);
    }
    const tick = () => {
      const remaining = Math.max(0, endTime - Date.now());
      const min = Math.floor(remaining / 60000);
      const sec = Math.floor((remaining % 60000) / 1000);
      const display = String(min).padStart(2, '0') + ':' + String(sec).padStart(2, '0');
      document.querySelectorAll('[data-cr-countdown]').forEach(el => el.textContent = display);

      if (remaining <= 0) {
        // Expiró: reiniciar inmediatamente con un nuevo bloque
        if (STATE.countdownInterval) {
          clearInterval(STATE.countdownInterval);
          STATE.countdownInterval = null;
        }
        endTime = Date.now() + (CONFIG.countdownSeconds * 1000);
        writeEndTime(endTime);
        STATE.countdownInterval = setInterval(tick, 1000);
      }
    };
    if (STATE.countdownInterval) clearInterval(STATE.countdownInterval);
    tick();
    STATE.countdownInterval = setInterval(tick, 1000);
  }

  // ---------- Refresh ----------
  let scheduled = null;
  function schedule(delay) {
    if (scheduled) clearTimeout(scheduled);
    // Soporta delay=0 explícito (no caer al fallback de 200ms con `|| 200`)
    const ms = (delay == null) ? 200 : delay;
    scheduled = setTimeout(run, ms);
  }

  async function run() {
    scheduled = null;
    const cart = await fetchCart();
    if (!cart) return;
    STATE.lastCart = cart;
    markGiftLineItems(cart);
    renderRewardsFromCart(cart);
    const mutated = await reconcileGifts();
    if (mutated) {
      const cart2 = await fetchCart();
      if (cart2) {
        STATE.lastCart = cart2;
        markGiftLineItems(cart2);
        renderRewardsFromCart(cart2);
      }
      schedule(300);
    }
  }

  // Detectar mutaciones del carrito vía fetch
  function installFetchHook() {
    if (window.__crFetchHookInstalled) return;
    const origFetch = window.fetch;
    window.fetch = function () {
      const arg0 = arguments[0];
      const url = (arg0 && arg0.url) || (typeof arg0 === 'string' ? arg0 : '');
      const isCartMutation = typeof url === 'string' &&
        /\/cart\/(add|change|update|clear)(\.js|$)/.test(url);
      const promise = origFetch.apply(this, arguments);
      // v3.10: también skip si hay un reconcile lock activo (defensa contra
      // race condition donde STATE.busy se resetea mientras reconcile sigue corriendo).
      if (isCartMutation && !STATE.busy && !_reconcileLock) {
        promise.then(() => schedule(150)).catch(() => {});
      }
      return promise;
    };
    window.__crFetchHookInstalled = true;
  }

  // MutationObserver: cuando Shrine Pro re-renderiza el drawer, re-marcar gift items
  function watchDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) {
      setTimeout(watchDrawer, 600);
      return;
    }
    let mutationDebounce = null;
    const obs = new MutationObserver(() => {
      // Re-marcar gift items inmediatamente (sin fetch)
      if (STATE.lastCart) markGiftLineItems(STATE.lastCart);
      // Debounce: tras 200ms sin más mutaciones, refrescar la barra desde /cart.js
      if (mutationDebounce) clearTimeout(mutationDebounce);
      mutationDebounce = setTimeout(async () => {
        if (STATE.busy) return;
        const cart = await fetchCart();
        if (!cart) return;
        STATE.lastCart = cart;
        markGiftLineItems(cart);
        renderRewardsFromCart(cart);
      }, 200);
    });
    obs.observe(drawer, { childList: true, subtree: true });
  }

  // ---------- XHR hook (defensa: capta cualquier mutación de carrito vía XMLHttpRequest) ----------
  function installXhrHook() {
    if (window.__crXhrHookInstalled) return;
    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__crUrl = url || '';
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      const url = this.__crUrl || '';
      const isCartMutation = typeof url === 'string' &&
        /\/cart\/(add|change|update|clear)(\.js|$)/.test(url);
      // v3.10: también skip si hay un reconcile lock activo (misma defensa que en fetch hook).
      if (isCartMutation && !STATE.busy && !_reconcileLock) {
        this.addEventListener('load', () => schedule(150));
      }
      return origSend.apply(this, arguments);
    };
    window.__crXhrHookInstalled = true;
  }

  // ---------- Listener: kit:complete (Crea Tu Kit completa 3 items) ----------
  // kit-builder dispara este evento ANTES de abrir el drawer (con 900ms de delay).
  // Forzamos reconcile inmediato para que cuando el drawer abra, el cart real
  // ya tenga los regalos y EasySell los lea correctamente.
  function installKitCompleteListener() {
    if (window.__crKitListenerInstalled) return;
    const tryInstall = () => {
      const KB = window.KitBuilder;
      if (KB && KB.events && typeof KB.events.addEventListener === 'function') {
        KB.events.addEventListener('kit:complete', () => {
          // Reconcile inmediato — sin debounce; tenemos ~900ms de margen
          // antes de que kit-builder llame drawer.open().
          schedule(0);
        });
        // También reconcile cada vez que el state cambia (defensa adicional)
        KB.events.addEventListener('kit:change', () => schedule(120));
        window.__crKitListenerInstalled = true;
        return true;
      }
      return false;
    };
    if (!tryInstall()) {
      // KitBuilder puede cargar después; reintentos cortos
      setTimeout(tryInstall, 300);
      setTimeout(tryInstall, 1000);
      setTimeout(tryInstall, 2500);
    }
  }

  // ---------- Listener: cart-drawer abierto (catch-all para cualquier flujo) ----------
  // Cuando el drawer pasa de cerrado a abierto, garantizamos un reconcile
  // antes de que el usuario tenga tiempo de hacer click en checkout/EasySell.
  function installDrawerOpenListener() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) {
      setTimeout(installDrawerOpenListener, 500);
      return;
    }
    let prevOpen = drawer.classList.contains('active');
    const obs = new MutationObserver(() => {
      const nowOpen = drawer.classList.contains('active');
      if (nowOpen && !prevOpen) {
        // Drawer recién abierto → reconcile inmediato
        schedule(0);
      }
      prevOpen = nowOpen;
    });
    obs.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  async function init() {
    if (STATE.initialized) return;
    STATE.initialized = true;
    installFetchHook();
    installXhrHook();
    installKitCompleteListener();
    installDrawerOpenListener();
    watchDrawer();
    startCountdown();
    const cart = await fetchCart();
    if (cart) {
      STATE.lastCart = cart;
      markGiftLineItems(cart);
      renderRewardsFromCart(cart);
    }
    await reconcileGifts();
  }

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.SereneCartRewards = {
    config: CONFIG,
    state: STATE,
    refresh: run,
    get lastCart() { return STATE.lastCart; },
  };
})();
